import moment from "moment";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQuery } from "react-query";
import { findServiceTaxNote } from "../../../../repositories/qerpapi/erp/relatorio/NotaFiscalServicoRepository";
import { findClientsV3 } from "../../../../repositories/qerpapi/v3/ClienteRepository";
import { findCompaniesV3 } from "../../../../repositories/qerpapi/v3/EmpresaRepository";
import { notify } from "../../../../utils/notify";
import { periods } from "../../../financeiro/constants";
import { initialTaxFilter, taxNotesColumns } from "../constants";

const NotaServicoContext = createContext();

const NotaServicoContextProvider = ({ children }) => {
  const [filters, setFilters] = useState({ ...initialTaxFilter });
  const [filterPeriod, setFilterPeriod] = useState({
    ...initialTaxFilter.searchDate,
  });

  const [columns, setColumns] = useState(taxNotesColumns);

  const [isMainFetchEnabled, setMainFetchEnabled] = useState(false);

  const [cachedCompanies, setCachedCompanies] = useState({});
  const [cachedClients, setCachedClients] = useState([]);
  const [cachedNotes, setCachedNotes] = useState({
    data: [],
    period: {},
  });

  // ----------------------------------------------------------------------------

  const setTaxNoteData = useCallback(
    (data) => setCachedNotes((prev) => ({ ...prev, ...data })),
    []
  );

  // ----------------------------------------------------------------------------

  const setSelectedCompany = useCallback(
    (selectedCompany) =>
      setFilters((prev) => ({ ...prev, multicompany: selectedCompany })),
    []
  );

  const setSelectedCustomer = useCallback(
    (selectedCustomer) =>
      setFilters((prev) => ({ ...prev, taker: selectedCustomer })),
    []
  );

  const setCustomDatePeriod = useCallback((newValue) => {
    setFilterPeriod((prev) => {
      return {
        ...prev,
        ...newValue,
      };
    });
  }, []);

  const setPeriod = useCallback(
    (newValue) => {
      if (newValue === periods.Personalizado) {
        setCustomDatePeriod({
          startDate: undefined,
          endDate: undefined,
        });
      }

      setFilters((prev) => ({
        ...prev,
        period: newValue,
      }));
    },
    [setCustomDatePeriod]
  );

  const setCustomSearchDate = useCallback((newValue) => {
    setFilters((prev) => ({
      ...prev,
      startDate: newValue?.startDate
        ? moment(newValue?.startDate).format("YYYY-MM-DD")
        : moment().startOf("month").format("YYYY-MM-DD"),
      endDate: newValue?.endDate
        ? moment(newValue?.endDate).format("YYYY-MM-DD")
        : moment().endOf("month").format("YYYY-MM-DD"),
    }));
  }, []);

  const setMulticompany = useCallback((newValue) => {
    setFilters((prev) => ({ ...prev, multicompany: newValue }));
  }, []);

  const setNoteNumber = useCallback((newValue) => {
    setFilters((prev) => ({ ...prev, noteNumber: newValue }));
  }, []);

  const setRpsNumber = useCallback((newValue) => {
    setFilters((prev) => ({ ...prev, rpsNumber: newValue }));
  }, []);

  const setStatus = useCallback((newValue) => {
    setFilters((prev) => ({ ...prev, status: newValue }));
  }, []);

  const setMultipleSort = useCallback((sortValues) => {
    setFilters((prev) => ({ ...prev, multipleSort: sortValues }));
  }, []);

  // ----------------------------------------------------------------------------

  const formatSortedLabel = useCallback((sortFilter) => {
    const sort = JSON.parse(sortFilter);
    if (!sort.length) return "Data Emissão";

    const sortedLabels = taxNotesColumns
      .filter((column) => sort.find((sorted) => sorted.column === column.id))
      .map((item) => item.label);
    return sortedLabels.join(", ");
  }, []);

  const handleSetSort = useCallback(
    (sorted) => {
      const sortedColumns = JSON.parse(filters.multipleSort);
      const sortedIndex = sortedColumns.findIndex(
        (value) => sorted.column === value.column
      );
      if (sorted.direction === "disabled" && sortedIndex >= 0) {
        sortedColumns.splice(sortedIndex, 1);
        return setMultipleSort(JSON.stringify(sortedColumns));
      }

      if (sortedIndex >= 0) {
        sortedColumns[sortedIndex] = sorted;
        return setMultipleSort(JSON.stringify(sortedColumns));
      }

      sortedColumns.push(sorted);
      setMultipleSort(JSON.stringify(sortedColumns));
    },
    [filters.multipleSort, setMultipleSort]
  );

  const handleSort = useCallback(
    (sorted) => {
      const sortedColumns = columns.map((column) => {
        if (column.id === sorted.id) {
          if (column.sorting === "disabled") {
            handleSetSort({ column: column.id, direction: "asc" });
            return {
              ...column,
              sorting: "asc",
            };
          }

          if (column.sorting === "asc") {
            handleSetSort({ column: column.id, direction: "desc" });
            return {
              ...column,
              sorting: "desc",
            };
          }

          if (column.sorting === "desc") {
            handleSetSort({ column: column.id, direction: "disabled" });
            return {
              ...column,
              sorting: "disabled",
            };
          }
        }
        return column;
      });

      setColumns(sortedColumns);
    },
    [columns, handleSetSort]
  );

  // ----------------------------------------------------------------------------

  const applyPeriod = useCallback(
    (periodDates) => {
      setCustomSearchDate(periodDates);
      setTaxNoteData({ period: periodDates });
      setCustomDatePeriod({
        startDate: periodDates?.startDate
          ? moment(periodDates.startDate, "YYYY-MM-DD").toDate()
          : moment().startOf("month").toDate(),
        endDate: periodDates?.endDate
          ? moment(periodDates.endDate, "YYYY-MM-DD").toDate()
          : moment().endOf("month").toDate(),
      });
    },
    [setTaxNoteData, setCustomDatePeriod, setCustomSearchDate]
  );

  useEffect(() => {
    setCustomSearchDate(filterPeriod);
  }, [filterPeriod, setCustomSearchDate]);

  // ----------------------------------------------------------------------------

  const handleMainData = useCallback(
    async (res) => {
      const data = res?.data;
      if (!res?.success) {
        if (res.statusCode === 401) {
          notify("Token expirado", true, "error");
        }
        notify(res.message, true, "error");
        return;
      }

      setTaxNoteData({
        data: data.movements ?? [],
      });
      applyPeriod(data?.searchPeriod);
    },
    [applyPeriod, setTaxNoteData]
  );

  const { isLoading, isRefetching, refetch } = useQuery(
    "nota-servico",
    () => findServiceTaxNote(filters),
    {
      enabled: !!sessionStorage.getItem("gfAccessToken") && isMainFetchEnabled,
      onSuccess: handleMainData,
    }
  );

  // ----------------------------------------------------------------------------

  const validateIfCompanyIdExistAndSetNewDefault = useCallback(
    async (userCompanies) => {
      const companiesIds = Object.values(userCompanies);

      if (!companiesIds.includes(filters.multicompany)) {
        setSelectedCompany(userCompanies.Todos);
      }
    },
    [filters.multicompany, setSelectedCompany]
  );

  const handleCompaniesData = useCallback(
    async (companies) => {
      const data = companies?.data ?? [];

      if (!companies.success) notify(companies.message, true, "error");

      if (companies?.data) {
        const allCompaniesString = data.map((company) => company.id).join(",");
        const formatedCompanies = {
          Todos: allCompaniesString,
        };

        data.forEach(
          (company) => (formatedCompanies[company.nomeempresa] = company.id)
        );

        await validateIfCompanyIdExistAndSetNewDefault(formatedCompanies);

        setCachedCompanies(formatedCompanies);
        setMainFetchEnabled(true);
      }
    },
    [validateIfCompanyIdExistAndSetNewDefault]
  );

  const { isLoading: isCompanyLoading } = useQuery(
    "empresas",
    findCompaniesV3,
    {
      enabled: !!sessionStorage.getItem("gfAccessToken"),
      onSuccess: handleCompaniesData,
    }
  );

  // ----------------------------------------------------------------------------

  const handleClientsData = useCallback(async (clients) => {
    const data = clients?.data ?? [];

    if (!clients.success) return notify(clients.message, true, "error");

    const formatedClients = data.map((client) => ({
      description: client.razao,
      value: client.cpfcnpj,
    }));

    setCachedClients(formatedClients);
  }, []);

  const { isLoading: isClientLoading } = useQuery("clientes", findClientsV3, {
    enabled: !!sessionStorage.getItem("gfAccessToken"),
    onSuccess: handleClientsData,
  });

  // ----------------------------------------------------------------------------

  const contextValue = useMemo(
    () => ({
      filters,
      filterPeriod,
      columns,

      setSelectedCompany,
      setSelectedCustomer,
      setPeriod,
      setMulticompany,
      setNoteNumber,
      setStatus,
      setRpsNumber,

      setMultipleSort,

      setCustomDatePeriod,

      handleSort,
      formatSortedLabel,

      cachedNotes,
      cachedCompanies,
      cachedClients,

      isLoading,
      isRefetching,
      isCompanyLoading,
      isClientLoading,

      refetch,
    }),
    [
      filters,
      filterPeriod,
      columns,
      setSelectedCompany,
      setSelectedCustomer,
      setPeriod,
      setMulticompany,
      setNoteNumber,
      setStatus,
      setRpsNumber,

      setMultipleSort,

      setCustomDatePeriod,

      handleSort,
      formatSortedLabel,
      cachedNotes,
      cachedCompanies,
      cachedClients,

      isLoading,
      isRefetching,
      isCompanyLoading,
      isClientLoading,
      refetch,
    ]
  );

  return (
    <NotaServicoContext.Provider value={contextValue}>
      {children}
    </NotaServicoContext.Provider>
  );
};

const useNotaServicoService = () => {
  const context = useContext(NotaServicoContext);

  if (!context)
    throw new Error(
      "useNotaServicoService must be used with NotaServicoContextProvider"
    );

  return context;
};

export { NotaServicoContextProvider, useNotaServicoService };
